import I18n from 'i18n-js';
import {
  CASH_BY_RECIPIENT,
  CASH_BY_SENDER,
  PAYMENT_TYPE_GRABPAY,
  POSTPAID_BILLING_TYPE,
} from 'constants/booking-constants';
import _get from 'lodash/get';
import formatCurrency from 'common/utils/format-currency';
import paymentIcon from 'components/booking/payment-field/payment-icon';
import { getDeliveryFinalDropOffs } from 'helper/anytime-booking';

export const CashPaymentMethods = [
  {
    type: CASH_BY_SENDER,
    paymentTypeID: CASH_BY_SENDER,
  },
  {
    type: CASH_BY_RECIPIENT,
    paymentTypeID: CASH_BY_RECIPIENT,
  },
];

export const generatePaymentMethodToRender = (
  paymentMethod,
  delivery,
  city,
  options = {
    isCashlessPaymentDisabled: false,
    isCashPaymentDisabled: false,
    totalAmount: undefined,
    onRetryFailedPayments: undefined,
    onReloadPaymentMethods: undefined,
  },
) => {
  const {
    isCashlessPaymentDisabled = false,
    isCashPaymentDisabled = false,
    totalAmount = undefined,
    onRetryFailedPayments,
    onReloadPaymentMethods,
  } = options;

  const serviceAllowCash = _get(delivery, 'config.paymentMethods', [
    'CASH',
  ]).includes('CASH');
  const serviceAllowCard = _get(delivery, 'config.paymentMethods', [
    'CASH',
  ]).includes('CARD');

  const isSameDayService =
    _get(delivery, 'detail.configuration.serviceType') === 'SAME_DAY';
  const dropOffs = getDeliveryFinalDropOffs(_get(delivery, 'detail'));
  const isMSD = _get(dropOffs, 'length', 0) > 1;

  const cashlessPayment = !(
    paymentMethod.paymentTypeID === CASH_BY_SENDER ||
    paymentMethod.paymentTypeID === CASH_BY_RECIPIENT
  );

  const paymentMethodToRender = {
    type: paymentMethod.type,
    paymentTypeID: paymentMethod.paymentTypeID,
    isPrimary: !!paymentMethod.isPrimary,
    icon: '',
    disabled: !paymentMethod.enabled,
    disabledReason: undefined,
    ctaText: undefined,
    ctaFunc: undefined,
    cashlessPayment,
  };

  if (cashlessPayment && isCashlessPaymentDisabled) {
    paymentMethodToRender.disabled = true;
    paymentMethodToRender.disabledReason = I18n.t(
      'v2.failedPayments.bookingPopoverText',
    );
    paymentMethodToRender.ctaText = I18n.t(
      'v2.failedPayments.bookingPopoverButton',
    );
    paymentMethodToRender.ctaFunc = onRetryFailedPayments;
  }

  switch (paymentMethod.type) {
    case PAYMENT_TYPE_GRABPAY: {
      const isOVO = _get(city, 'countryCode') === 'ID';
      const isMOCA = _get(city, 'countryCode') === 'VN';

      if (isOVO) {
        paymentMethodToRender.icon = paymentIcon.OVO;
      } else if (isMOCA) {
        paymentMethodToRender.icon = paymentIcon.MOCA;
      } else {
        paymentMethodToRender.icon = paymentIcon.grabpay;
      }

      const balance = _get(paymentMethod, 'payload.Balance');
      paymentMethodToRender.displayName = formatCurrency({
        code: _get(paymentMethod, 'payload.Currency'),
        amount: balance,
      });

      if (!paymentMethodToRender.disabled) {
        if (totalAmount === undefined || balance < totalAmount) {
          paymentMethodToRender.disabled = true;
          paymentMethodToRender.disabledReason = isOVO
            ? I18n.t('ovo.insufficientBalance.popOverText')
            : I18n.t('v2.failedPayments.grabPay.popOverText');
          paymentMethodToRender.ctaText = isOVO
            ? I18n.t('ovo.insufficientBalance.bookingPopoverButton')
            : I18n.t('v2.failedPayments.grabPay.bookingPopoverButton');
          paymentMethodToRender.ctaFunc = onReloadPaymentMethods;
        }
      }

      break;
    }

    case POSTPAID_BILLING_TYPE: {
      paymentMethodToRender.icon = paymentIcon.postpaidBilling;
      paymentMethodToRender.displayName = I18n.t(
        'booking.paymentMethod.corpBilling',
      );
      break;
    }

    case CASH_BY_SENDER: {
      paymentMethodToRender.icon = paymentIcon.CASH;
      paymentMethodToRender.displayName = I18n.t(
        'booking.paymentMethod.cashBySender',
      );
      paymentMethodToRender.disabled =
        !serviceAllowCash || (!isSameDayService && isCashPaymentDisabled);
      break;
    }

    case CASH_BY_RECIPIENT: {
      paymentMethodToRender.icon = paymentIcon.CASH;
      paymentMethodToRender.displayName = I18n.t(
        'booking.paymentMethod.cashByRecipient',
      );
      paymentMethodToRender.disabled =
        !serviceAllowCash ||
        (!isSameDayService && isCashPaymentDisabled) ||
        isMSD;
      break;
    }

    default: {
      // credit cards
      const { type, refNumber } = paymentMethod;
      const accountIcon = _get(paymentMethod, 'payload.AccountIcon');
      paymentMethodToRender.icon =
        accountIcon || paymentIcon[type] || paymentIcon.generic;
      paymentMethodToRender.displayName = refNumber;
      paymentMethodToRender.disabled =
        paymentMethodToRender.disabled || !serviceAllowCard;
      break;
    }
  }

  return paymentMethodToRender;
};
